/**
 * Brief 80 — AttestationFlow modal
 *
 * Lets the calling staff member attest to the current version of an
 * MLCO-owned policy. Posts to /api/policy/<slug>/attest with the resolved
 * current policy_version_id and an optional signature body + reason.
 */

import React, { useEffect, useState } from "react";
import { X, PenTool, AlertCircle } from "lucide-react";
import { authFetch } from "../../config/api";

// BM-H3260 (29 Sep 2026): authFetch, not raw fetch. Raw fetch
// sends no X-Bonsai-User-Id, so this call was authenticated only when the
// browser held a bonsai_session cookie, which a password sign-in does not
// set. The /policy/* surface was moved to authFetch for exactly this
// (see config/api.js); this file was missed.
const API = process.env.REACT_APP_BACKEND_URL || "";

export default function AttestationFlow({ onClose }) {
  const [slug, setSlug] = useState("aml-policy");
  const [policy, setPolicy] = useState(null);
  const [signatureBody, setSignatureBody] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const r = await authFetch(`${API}/api/policy/${slug}`, { credentials: "include" });
        if (r.ok) setPolicy(await r.json());
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [slug]);

  async function submit() {
    if (!policy?.version?._id) {
      setError("Cannot resolve current version for this policy");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const r = await authFetch(`${API}/api/policy/${slug}/attest`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          policy_version_id: policy.version._id,
          signature_body: signatureBody || null,
          reason: reason || null,
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status} — ${await r.text()}`);
      const j = await r.json();
      setSuccess(`Attested to ${j.policy_slug} ${j.policy_version_label}. Expires ${j.expires_at}.`);
      setTimeout(onClose, 1500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div data-testid="attestation-modal" className="bg-white rounded shadow-lg w-full max-w-2xl">
        <div className="flex items-center justify-between p-3 border-b">
          <h3 className="font-semibold flex items-center gap-2"><PenTool size={16} /> Attest to a policy</h3>
          <button type="button" onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X size={16} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-sm font-medium" htmlFor="attest-slug">Policy</label>
            <select
              id="attest-slug"
              data-testid="attestation-slug-select"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="w-full mt-1 border rounded p-2"
            >
              <option value="aml-policy">aml-policy</option>
              <option value="governance-policy">governance-policy</option>
            </select>
          </div>
          {loading ? (
            <p className="text-sm text-gray-500">Loading current version</p>
          ) : policy?.version ? (
            <p className="text-sm text-gray-700" data-testid="attestation-version-banner">
              Current version: <span className="font-mono">{policy.version.version_label}</span>
            </p>
          ) : (
            <p className="text-sm text-red-700">No current version found</p>
          )}
          <div>
            <label className="text-sm font-medium" htmlFor="sig-body">Signature body (optional)</label>
            <textarea
              id="sig-body"
              data-testid="attestation-signature-body"
              value={signatureBody}
              onChange={(e) => setSignatureBody(e.target.value)}
              rows={3}
              className="w-full mt-1 border rounded p-2 font-mono text-sm"
            />
          </div>
          <div>
            <label className="text-sm font-medium" htmlFor="att-reason">Reason (optional)</label>
            <input
              id="att-reason"
              data-testid="attestation-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Annual SRA review"
              className="w-full mt-1 border rounded p-2"
            />
          </div>
          {error && (
            <div data-testid="attestation-error" className="text-sm text-red-700 flex items-center gap-1">
              <AlertCircle size={14} /> {error}
            </div>
          )}
          {success && (
            <div data-testid="attestation-success" className="text-sm text-green-700">{success}</div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              data-testid="attestation-cancel"
              onClick={onClose}
              className="px-3 py-1.5 text-sm border rounded"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="attestation-submit"
              onClick={submit}
              disabled={submitting || !policy?.version?._id}
              className="px-3 py-1.5 text-sm border rounded bg-gray-900 text-white disabled:opacity-50"
            >
              Attest
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
